/**
 * Chat messages with the chat modal's markup (chatModal.js / chatModal.css): the
 * user's message on the right with the user icon, the answer on the left (in Test
 * next to the violet "AI Agent" badge); markdown (links, lists, tables) renders
 * with the chat modal's styles. The bubble colours for the grey Studio surface are
 * set in agentStudio.css (.as-cm). Raw HTML from the model is not rendered.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useTranslation } from 'react-i18next';
import ProductCarousel from '../ProductCarousel';
import '../chatModal.css';

const LinkRenderer = (p) => (
  <a href={p.href} target="_blank" rel="noopener noreferrer">{p.children}</a>
);

function Md({ text }) {
  return (
    <ReactMarkdown remarkPlugins={[[remarkGfm, {}], [remarkBreaks, {}]]} components={{ a: LinkRenderer }}>
      {text || ''}
    </ReactMarkdown>
  );
}

export function UserMessage({ text, children }) {
  const { t } = useTranslation();
  return (
    <div className="message user-message as-cm">
      <div className="user-chat-text">{children || <Md text={text} />}</div>
      <div className="user-icon-container">
        <img src="/user-icon.png" alt={t('chat_user_icon_alt')} className="user-icon" />
      </div>
    </div>
  );
}

/**
 * `below`: rendered under the answer, aligned with its text (e.g. the meta line).
 * `avatar`: the "AI Agent" badge stands for the customer's agent (Test); the
 * builder in Build has none.
 * `onClick`: on the bubble, e.g. to show another answer's details in the Test panel.
 */
// products: the agent's product cards for this answer, shown under it (as in the chat widget)
export function BotMessage({ text, children, below, products, className = '', avatar = true, onClick }) {
  const { t } = useTranslation();
  return (
    <div className={`message bot-message as-cm ${avatar ? '' : 'no-avatar'} ${className}`}>
      {avatar && (
        <div className="scf-icon-container">
          <div className="bot-left-icon">
            <div className="bot-left-icon-text">
              <p style={{ fontSize: '11px', lineHeight: '1.05' }}>AI</p>
              <p>{t('chat_bot_icon_agent')}</p>
            </div>
          </div>
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="bot-chat-text" onClick={onClick}>{children || <Md text={text} />}</div>
      {products?.length > 0 && <div className="as-cm-products"><ProductCarousel products={products} /></div>}
      {below && <div className="as-cm-below">{below}</div>}
    </div>
  );
}
